/**
 * LLM Configuration API Client
 */
import apiClient from "./client";
import {
  LLMConfiguration,
  LLMConfigCreate,
  LLMConfigUpdate,
  TestConnectionRequest,
  TestConnectionResponse,
} from "../types/llm.types";

export interface LLMConfigListResponse {
  configs: LLMConfiguration[];
  total: number;
}

const llmConfigsApi = {
  /**
   * Get all LLM configurations for current user
   */
  getAllConfigs: async (): Promise<LLMConfigListResponse> => {
    const response = await apiClient.get<LLMConfigListResponse>(
      "/llm-configs"
    );
    return response.data;
  },

  /**
   * Get LLM configuration for a specific provider
   */
  getConfigByProvider: async (
    provider: string
  ): Promise<LLMConfiguration> => {
    const response = await apiClient.get<LLMConfiguration>(
      `/llm-configs/${provider}`
    );
    return response.data;
  },

  /**
   * Create or update LLM configuration
   */
  createOrUpdateConfig: async (
    config: LLMConfigCreate
  ): Promise<LLMConfiguration> => {
    const response = await apiClient.post<LLMConfiguration>(
      "/llm-configs",
      config
    );
    return response.data;
  },

  /**
   * Update LLM configuration by ID (for editing Azure configs)
   */
  updateConfigById: async (
    config_id: string,
    config: LLMConfigUpdate
  ): Promise<LLMConfiguration> => {
    const response = await apiClient.patch<LLMConfiguration>(
      `/llm-configs/by-id/${config_id}`,
      config
    );
    return response.data;
  },

  /**
   * Partially update LLM configuration by provider (deprecated for Azure)
   */
  updateConfig: async (
    provider: string,
    config: LLMConfigUpdate
  ): Promise<LLMConfiguration> => {
    const response = await apiClient.patch<LLMConfiguration>(
      `/llm-configs/${provider}`,
      config
    );
    return response.data;
  },

  /**
   * Delete LLM configuration by ID
   */
  deleteConfig: async (config_id: string): Promise<void> => {
    await apiClient.delete(`/llm-configs/${config_id}`);
  },

  /**
   * Set configuration as default by ID
   */
  setDefaultConfig: async (
    config_id: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      `/llm-configs/${config_id}/set-default`
    );
    return response.data;
  },

  /**
   * Test LLM connection
   */
  testConnection: async (
    testData: TestConnectionRequest
  ): Promise<TestConnectionResponse> => {
    const response = await apiClient.post<TestConnectionResponse>(
      "/llm-configs/test-connection",
      testData
    );
    return response.data;
  },
};

export default llmConfigsApi;
